import React, { Component, Fragment } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import _ from "lodash";
import { withTheme, withStyles } from "@material-ui/core/styles";
import { Searcher } from "@openimis/fe-core";
import PreauthorizationFilter from "./PreauthorizationFilter";
import {
  withModulesManager,
  formatMessageWithValues,
  formatMessage,
  PublishedComponent,
} from "@openimis/fe-core";
import { fetchMyPreauthorization } from "../actions";

const PREAUTHORIZATION_SEARCHER_CONTRIBUTION_KEY = "preauthorization.Searcher";

const styles = (theme) => ({});

class PreAuthorizationSearcher extends Component {
  state = {
    filters: {}, // The filters state will dynamically hold selected filter values
  };

  constructor(props) {
    super(props);
    this.rowsPerPageOptions = [10, 20, 50, 100];
    this.defaultPageSize = 100;
  }

  // Handle filter changes for any field
  handleFilter = (key, value) => {
    this.setState(
      (prevState) => ({
        filters: {
          ...prevState.filters,
          [key]: value, // Update the filters object with the selected field
        },
      }),
      () => {
        this.fetch(); // Re-fetch the data whenever a filter is applied
      }
    );
  };

  // Fetch preauthorizations based on current filters
  fetch = () => {
    const { filters } = this.state;
    // Pass the filters to the API or data-fetching logic to filter preauthorizations
    this.props.fetchMyPreauthorization(this.props.modulesManager, filters);
  };

  // Function to compare preauthorizations by validity date (descending order)
  compareByValidityFrom = (a, b) => {
    const dateA = new Date(a.validityFrom);
    const dateB = new Date(b.validityFrom);
    return dateB - dateA; // Sort in descending order
  };

  // Headers for the table display
  headers = () => {
    return [
      "preauthorization.RefferingHF",
      "preauthorization.ReceivingHF",
      "preauthorization.Enrollee",
      "preauthorization.Diagnosis",
      "preauthorization.MedicalService",
      "preauthorization.Status",
    ].map((key) => formatMessage(this.props.intl, "preauthorization", key));
  };

  // Sorts configuration for table columns
  sorts = () => {
    return [
      ["refHfTo", true],
      ["refHfFrom", true],
      ["enroleee", true],
      ["icd", true],
      ["service", true],
      ["praStatus", true],
    ];
  };

  // Item formatters to display data in the table
  itemFormatters = () => {
    return [
      (c) => (
        <PublishedComponent
          readOnly={true}
          pubRef="location.HealthFacilityPicker"
          withLabel={false}
          value={c?.refHfTo}
        />
      ),
      (c) => (
        <PublishedComponent
          readOnly={true}
          pubRef="location.HealthFacilityPicker"
          withLabel={false}
          value={c?.refHfFrom}
        />
      ),
      (c) => (
        <PublishedComponent
          readOnly={true}
          pubRef="insuree.InsureePicker"
          withLabel={false}
          value={c?.insuree}
        />
      ),
      (c) => (
        <PublishedComponent
          readOnly={true}
          pubRef="medical.DiagnosisPicker"
          withLabel={false}
          value={c?.icd}
        />
      ),
      (c) => c?.service?.name || "",
      (c) =>
        formatMessage(
          this.props.intl,
          "preauthorization",
          `preauthorization.Status.${c.praStatus}`
        ),
    ];
  };

  render() {
    const {
      intl,
      preauthorizations,
      preAuthorizationPageInfo,
      fetchingPreAuthorization,
      fetchedPreAuthorization,
      errorPreAuthorization,
      defaultFilters,
      cacheFiltersKey,
      onDoubleClick,
      actionsContributionKey,
    } = this.props;

    // Sort preauthorizations by validity date
    const sortedPreauths = preauthorizations
      ? [...preauthorizations].sort(this.compareByValidityFrom)
      : [];

    // Filter preauthorizations based on selected filters
    const filteredPreauths = sortedPreauths.filter((item) => {
      return Object.entries(this.state.filters).every(([key, filterValue]) => {
        if (!filterValue) return true;

        const itemValue = item[key];

        // Handle different field comparisons
        if (typeof filterValue === "object") {
          if (filterValue?.code && itemValue?.code) {
            return filterValue.code === itemValue.code;
          }
          if (filterValue?.uuid && itemValue?.uuid) {
            return filterValue.uuid === itemValue.uuid;
          }
          if (filterValue?.id && itemValue?.id) {
            return filterValue.id === itemValue.id;
          }
          return false;
        }

        return itemValue === filterValue;
      });
    });

    return (
      <Fragment>
        {/* Filter Component to handle user input for filters */}
        <PreauthorizationFilter
          handleFilter={this.handleFilter} // Trigger filter changes
          filters={this.state.filters} // Pass current filter values
        />
        {/* Searcher to display filtered results */}
        <Searcher
          module="preauthorization"
          defaultFilters={defaultFilters}
          cacheFiltersKey={cacheFiltersKey}
          items={filteredPreauths} // Use filtered data
          itemsPageInfo={preAuthorizationPageInfo}
          fetchingItems={fetchingPreAuthorization}
          fetchedItems={fetchedPreAuthorization}
          errorItems={errorPreAuthorization}
          contributionKey={PREAUTHORIZATION_SEARCHER_CONTRIBUTION_KEY}
          tableTitle={formatMessageWithValues(
            intl,
            "preauthorization",
            "Preauthorization.found",
            { count: filteredPreauths.length }
          )}
          rowsPerPageOptions={this.rowsPerPageOptions}
          defaultPageSize={this.defaultPageSize}
          fetch={this.fetch}
          rowIdentifier={(r) => r.uuid}
          headers={this.headers}
          itemFormatters={this.itemFormatters}
          defaultOrderBy="validityFrom"
          sorts={this.sorts}
          onDoubleClick={onDoubleClick}
          actionsContributionKey={actionsContributionKey}
        />
      </Fragment>
    );
  }
}

// Map state to props to get preauthorizations and other necessary data
const mapStateToProps = (state) => ({
  preauthorizations: state.preauthorization.preauthorizations,
  preAuthorizationPageInfo: state.preauthorization.preAuthorizationPageInfo,
  fetchingPreAuthorization: state.preauthorization.fetchingPreAuthorization,
  fetchedPreAuthorization: state.preauthorization.fetchedPreAuthorization,
  errorPreAuthorization: state.preauthorization.errorPreAuthorization,
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ fetchMyPreauthorization }, dispatch);
};

export default withModulesManager(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(injectIntl(withTheme(withStyles(styles)(PreAuthorizationSearcher))))
);
